import { CurrentUserObject } from "@/common/hooks/useVisitorInfo";
import { useFollowUserMutation, useFollowingIdsQuery } from "@/common/queries/queries";
import { useCallback, useEffect, useState } from "react";
import { showNotification } from "@/components/alerts/Notifications";
import { metricsBus, EventSource } from "@/common/buses";
import { useAuthNavigate } from "@/common/hooks/useAuthNavigate";
import { useAnalyticsStore } from "@/common/store/analyticsStore";

interface UseFollow {
  currentUser: CurrentUserObject | null;
  username: string;
  gifId?: string;
  position?: number;
  playCount?: number;
}

export const useFollow = ({
  username,
  currentUser,
  gifId,
  position,
  playCount
}: UseFollow) => {
  const { data: followingIds = [] } = useFollowingIdsQuery();
  const followUserMutation = useFollowUserMutation();
  const [isLiked, setLiked] = useState<boolean>(false);
  const { navigateSignIn } = useAuthNavigate();
  const eventSource = useAnalyticsStore((state) => state.eventSource);

  useEffect(() => {
    setLiked(followingIds.includes(username));
  }, [followingIds, username]);

  const onFollow = useCallback(async () => {
    if (!currentUser) {
      navigateSignIn();
      return;
    }

    try {
      await followUserMutation.mutateAsync({ username, sourceId: eventSource?.id ?? null });
      metricsBus.emit("followUserClicked", {
        userName: username,
        source: eventSource?.name ?? EventSource.Other,
        sourceId: eventSource?.id ?? null,
        gifId,
        position,
        playCount
      });
      setLiked(true);
      showNotification({
        title: `Started following ${username}`,
        variant: "success"
      });
    } catch (err) {
      setLiked(false);
      // isLiked.current = false;
      showNotification({
        title: `Could not follow ${username}`,
        variant: "success"
      });
    }
  }, [eventSource, followUserMutation, username, currentUser, gifId, position, playCount]);

  return {
    onFollow,
    isLiked
  };
};
